// src/encounter/structured-note/note-add-action.ts
//
// PROD-3043 · frame v2 only · encounter UX law L10 (structure first; Samer, 6 Oct 20:00: "you just turned the
// template to grey boxes of free text"). No empty free-text input is on screen in any block. A block's
// narrative (the template's narrative item, "History notes", "Added notes") starts from ONE quiet "Add note"
// in the block's header actions; the line itself appears only once it is opened or written.
//
// The narrative is drawn deep inside the block (the A&P's "Added notes" is inside the A/P canvas), while the
// header belongs to the block (structured-note-section.tsx `StepBlock`). So the block PROVIDES a slot and the
// narrative, while it is empty, closed and editable, REGISTERS its opener in it. One narrative per block (the
// desk's one-line rule), so one registration.
//
// SIGNED: after the provider's signature, amending or not, nothing offers to add (rule 38): no "Add note",
// no "Add". The block provides that too, so every line below it reads the same answer.
import { createContext, useContext, useEffect, useRef } from 'react';

export interface AddNoteRequest {
  open: () => void;
  /** The narrative's anchor id: while it is closed, the header's "Add note" carries it, so a jump to the
   *  narrative (the Sign list, the chip rail) lands on its opener. */
  anchor?: string;
}

/** The block's "Add note" slot: a narrative registers its opener (or `null` to withdraw it). */
export const NoteAddActionContext = createContext<((request: AddNoteRequest | null) => void) | null>(null);

/** The provider has signed (records freeze, amending or not): nothing in the block offers to add. */
export const NoteSignedContext = createContext(false);

export const useNoteSigned = (): boolean => useContext(NoteSignedContext);

/** Is there a block "Add note" slot here? Outside a block there is no header to hold it, so a block
 *  narrative is drawn as before instead of collapsing to nothing. */
export const useHasAddNoteSlot = (): boolean => useContext(NoteAddActionContext) !== null;

/**
 * Offer `open` as the block's "Add note" while `active` (the narrative is empty, closed and editable).
 * Outside a block (no provider) it does nothing.
 */
export function useAddNoteAction(active: boolean, open: () => void, anchor?: string): void {
  const register = useContext(NoteAddActionContext);
  const latest = useRef(open);
  useEffect(() => {
    latest.current = open;
  });
  useEffect(() => {
    if (!register || !active) return;
    register({ open: () => latest.current(), anchor });
    return () => register(null);
  }, [register, active, anchor]);
}

/**
 * Export 21-10 (EncounterDocumentsStep, nurse-5-documents.html): a block whose list owns its own header actions
 * (the Documents step's Add order / Add document) draws them IN the block header, not on a row of their own. The
 * block provides a header node; the list portals its existing buttons into it (same handlers). `null` outside a
 * block, where the list keeps its own row.
 */
export const NoteBlockActionsHostContext = createContext<HTMLElement | null>(null);

export const useNoteBlockActionsHost = (): HTMLElement | null => useContext(NoteBlockActionsHostContext);
